import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from '@reach/router';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { allowedYearRange } from '../../../../../components/Inputs/YearRange/YearRange';
import PageContent from '../../../../../components/Layout/PageContent';
import LoadingContainer from '../../../../../components/LoadingContainer';
import { api } from '../../../../../core/api';
import { BenchmarkResultsInputRequest } from '../../../../../generated/model/benchmarkResultsInputRequest';
import { BenchmarkResultsView } from '../../../../../generated/model/benchmarkResultsView';
import { RangeOflong } from '../../../../../generated/model/rangeOflong';
import { useErrorsDispatch } from '../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import ResultsContainer from './ResultsContainer';
import ResultsInputTabs from './ResultsInput/ResultsInputTabs';
import { findLabelByValue } from './ResultsInput/TpPliValues';
import StatisticalAnalysisTable from './Tables/StatisticalAnalysisTable';
import TpPliTable from './Tables/TpPliTable';

interface TableProps {
  tpPliLabel: string;
  isWeighted: boolean;
  withAdjustments: boolean;
  yearRange: RangeOflong;
}

const ResultsPanel: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { benchmarkId } = useParams();
  const [isResultsLoading, setResultsLoading] = useState(false);
  const dispatchErrors = useErrorsDispatch();

  const [results, setResults] = useState<BenchmarkResultsView>();

  const [resultTableProps, setResultTableProps] = useState<TableProps>({
    tpPliLabel: '',
    isWeighted: false,
    withAdjustments: false,
    yearRange: allowedYearRange,
  });

  const totalObservationsCount =
    (resultTableProps.yearRange.to || allowedYearRange.to) -
    (resultTableProps.yearRange.from || allowedYearRange.from) +
    1;
  const hasMissingAdjustments = results?.adjustedResults?.profitLevelIndicatorResults?.some(
    (r) => r.numberOfObservations !== totalObservationsCount,
  );

  const fetchResults = async (formInput: BenchmarkResultsInputRequest) => {
    try {
      setResultsLoading(true);

      const data = await api.benchmark.fetchBenchmarkResults(benchmarkId, formInput);
      if (data) {
        setResults(data);
        setResultTableProps({
          tpPliLabel: findLabelByValue(t, formInput.profitLevelIndicator),
          isWeighted: formInput.indicatorType === 'WEIGHTED',
          withAdjustments: data.adjustedResults?.profitLevelIndicatorResults?.length !== 0,
          yearRange: formInput.yearRange || allowedYearRange,
        });
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    } finally {
      setResultsLoading(false);
    }
  };

  return (
    <PageContent>
      <Grid container>
        <Grid item xs={5}>
          <ResultsInputTabs fetchResults={fetchResults} isExportDisabled={!results} />
        </Grid>
        {isResultsLoading ? (
          <LoadingContainer />
        ) : (
          <>
            {results && (
              <Grid item xs={7}>
                <ResultsContainer>
                  <StatisticalAnalysisTable
                    {...resultTableProps}
                    statisticalResults={results?.primaryResults?.statisticalResults}
                    statisticalYearResults={results?.primaryResults?.statisticalYearResults}
                    testedPartyStatisticalResults={results?.testedPartyResults?.statisticalResults}
                  />
                </ResultsContainer>
              </Grid>
            )}

            {results && resultTableProps.withAdjustments && (
              <>
                <Grid item xs={5}></Grid>
                <Grid item xs={7}>
                  <ResultsContainer>
                    {hasMissingAdjustments && (
                      <Typography className={styles.missingAdjustmentsWarning}>
                        {t('benchmark:benchmarkResults.adjustments.missingAdjustmentsWarning')}
                      </Typography>
                    )}
                    <Typography className={styles.disclaimer}>
                      <p>{t('benchmark:benchmarkResults.adjustments.disclaimerP1')}</p>
                      <p>{t('benchmark:benchmarkResults.adjustments.disclaimerP2')}</p>
                    </Typography>
                    <StatisticalAnalysisTable
                      {...resultTableProps}
                      tpPliLabel={
                        t('benchmark:benchmarkResults.adjustments.adjustedPrefix') +
                        resultTableProps.tpPliLabel
                      }
                      statisticalResults={results?.adjustedResults?.statisticalResults}
                      statisticalYearResults={results?.adjustedResults?.statisticalYearResults}
                      testedPartyStatisticalResults={
                        results?.testedPartyResults?.statisticalResults
                      }
                    />
                  </ResultsContainer>
                </Grid>
              </>
            )}
            {results && (
              <Grid item xs={12}>
                <ResultsContainer>
                  <TpPliTable
                    {...resultTableProps}
                    results={results?.primaryResults?.profitLevelIndicatorResults}
                    resultsTp={results?.testedPartyResults?.results}
                  />
                </ResultsContainer>
              </Grid>
            )}

            {results && resultTableProps.withAdjustments && (
              <Grid item xs={12}>
                <ResultsContainer>
                  <TpPliTable
                    {...resultTableProps}
                    tpPliLabel={
                      t('benchmark:benchmarkResults.adjustments.adjustedPrefix') +
                      resultTableProps.tpPliLabel
                    }
                    results={results?.adjustedResults?.profitLevelIndicatorResults}
                    resultsTp={results?.testedPartyResults?.results}
                  />
                </ResultsContainer>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </PageContent>
  );
};

const useStyles = makeStyles(() => ({
  missingAdjustmentsWarning: {
    color: 'red',
    marginTop: '-4px',
    paddingBottom: '10px',
  },
  disclaimer: {
    marginTop: '-4px',
    paddingBottom: '10px',
  },
}));

export default ResultsPanel;
